/**
 * Execution Simulator component - displays mock execution results
 */

import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface ExecutionSimulatorProps {
  isExecuting: boolean;
  output: string | null;
  error: string | null;
  executionTime: number;
  onReset?: () => void;
}

export function ExecutionSimulator({
  isExecuting,
  output,
  error,
  executionTime,
  onReset,
}: ExecutionSimulatorProps) {
  const { t } = useTranslation("docs");
  if (!isExecuting && !output && !error) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-[#DDDDDD] bg-white overflow-hidden dark:bg-zinc-900">
      <div className="flex items-center justify-between bg-[#F5F5F5] px-4 py-2 border-b border-[#DDDDDD] dark:border-zinc-700 dark:bg-zinc-800">
        <span className="text-sm font-medium text-[#141A42] dark:text-zinc-100">
          {t("executionOutput")}
        </span>
        {executionTime > 0 && (
          <span className="text-xs text-gray-500">{executionTime}ms</span>
        )}
      </div>
      <div className="p-4">
        {isExecuting && (
          <div className="flex items-center gap-3 text-[#535EA4]">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">{t("executing")}</span>
          </div>
        )}

        {!isExecuting && error && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-300">
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">{t("error")}</span>
            </div>
            <pre className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 p-3 rounded border border-red-200 dark:border-red-800 overflow-x-auto">
              {error}
            </pre>
            {onReset && (
              <button
                onClick={onReset}
                className="text-sm text-[#535EA4] hover:underline"
              >
                {t("reset")}
              </button>
            )}
          </div>
        )}

        {!isExecuting && output && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-300">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">{t("success")}</span>
            </div>
            <pre className="text-sm text-gray-800 bg-[#F7FDF0] p-3 rounded border border-green-200 overflow-x-auto whitespace-pre-wrap dark:text-gray-200">
              {output}
            </pre>
            {onReset && (
              <button
                onClick={onReset}
                className="text-sm text-[#535EA4] hover:underline"
              >
                {t("reset")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
